export type DeviceKind = 'phone' | 'tablet' | 'desktop';

export type DeviceClassificationInput = {
  userAgent?: string | null;
  platform?: string | null;
  maxTouchPoints?: number | null;
  screenWidth?: number | null;
  screenHeight?: number | null;
  viewportWidth?: number | null;
  viewportHeight?: number | null;
};

const PHONE_SIDE_THRESHOLD = 500;

function normalizeNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function shortestSide(a: number | null, b: number | null): number | null {
  if (a == null || b == null) {
    return null;
  }
  return Math.min(a, b);
}

export function detectDeviceKind(input: DeviceClassificationInput = {}): DeviceKind {
  const userAgent = (input.userAgent ?? '').toLowerCase();
  const platform = (input.platform ?? '').toLowerCase();
  const maxTouchPoints =
    typeof input.maxTouchPoints === 'number' && Number.isFinite(input.maxTouchPoints)
      ? input.maxTouchPoints
      : 0;
  const hasTouch = maxTouchPoints > 0;

  const shortestScreenSide = shortestSide(
    normalizeNumber(input.screenWidth),
    normalizeNumber(input.screenHeight),
  );
  const shortestViewportSide = shortestSide(
    normalizeNumber(input.viewportWidth),
    normalizeNumber(input.viewportHeight),
  );

  const isiPadDesktopMode = platform === 'macintel' && hasTouch;
  if (/ipad|tablet/i.test(userAgent) || isiPadDesktopMode) {
    return 'tablet';
  }

  if (/iphone|ipod/i.test(userAgent)) {
    return 'phone';
  }

  if (/android/i.test(userAgent)) {
    return /mobile/i.test(userAgent) ? 'phone' : 'tablet';
  }

  if (/mobile/i.test(userAgent)) {
    return 'phone';
  }

  if (hasTouch) {
    if ((shortestScreenSide != null && shortestScreenSide <= PHONE_SIDE_THRESHOLD)
      || (shortestViewportSide != null && shortestViewportSide <= PHONE_SIDE_THRESHOLD)) {
      return 'phone';
    }
  }

  return 'desktop';
}

export function getCurrentDeviceKind(): DeviceKind {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'desktop';
  }

  return detectDeviceKind({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  });
}

export function shouldUseMobileApp(params: {
  featureEnabled: boolean;
  isMobileViewport: boolean;
  deviceKind: DeviceKind;
}): boolean {
  return Boolean(params.featureEnabled && (params.isMobileViewport || params.deviceKind === 'phone'));
}
